import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Bot } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { formatMicrocredits } from '../../../../utils/creditUnits';
import './PackageStore.css';

const money = pack => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: pack.currency.toUpperCase() }).format(pack.amountMinor / 100);

export default function CreditPackStore({ companyId, isOwner, entitlements, configured }) {
    const [packs, setPacks] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const companyRef = useRef(companyId);
    companyRef.current = companyId;
    const purchaseKeys = useRef({});
    const purchasing = useRef(false);
    const load = useCallback(async () => {
        if (!configured) return;
        try {
            const { data } = await axios.get(getApiUrl('subscription/credit-packs'));
            if (!Array.isArray(data)) throw new Error('Credit package response is incomplete');
            if (companyRef.current !== companyId) return;
            setPacks(data);
            setError('');
        } catch (_) {
            if (companyRef.current === companyId) setError('Kunde inte hämta kreditpaketen. Försök igen.');
        }
    }, [configured, companyId]);
    useEffect(() => {
        setPacks(null); setError(''); setBusy('');
        purchasing.current = false;
        purchaseKeys.current = {};
        load();
    }, [load]);
    const buy = async pack => {
        if (purchasing.current || !isOwner || entitlements?.billingAccess?.allowed === false) return;
        purchasing.current = true;
        setBusy(pack.package_version); setError('');
        const requestKey = purchaseKeys.current[pack.package_version] || uuidv4();
        purchaseKeys.current[pack.package_version] = requestKey;
        try {
            const { data } = await axios.post(getApiUrl(`${companyId}/subscription/credits/checkout`), { packageVersion: pack.package_version, requestKey });
            if (companyRef.current === companyId && data.url) window.location.assign(data.url);
        } catch (failure) {
            if (companyRef.current === companyId) setError(failure.response?.data?.error || 'Kunde inte öppna betalningen. Försök igen.');
        } finally {
            if (companyRef.current === companyId) { setBusy(''); purchasing.current = false; }
        }
    };
    if (!configured) return null;
    return <section className="modern-card package-store" id="krediter">
        <div className="modern-card-header"><div><h2 className="section-title"><Bot size={16} />Extra LedgerLoop AI-krediter</h2><p className="section-subtitle">Fyll på när du vill använda mer AI</p></div></div>
        <div className="modern-card-body">
            <p className="feature-description mb-3">Engångsköp utan ändring av din plan. Köpta krediter sparas mellan perioder. En aktiv prenumeration eller provperiod krävs för att använda dem.</p>
            {entitlements?.status === 'trial' && <p className="feature-description mb-3">AI-kreditpaket betalas även under provperioden. Krediterna läggs till efter bekräftad betalning. Köpet avslutar eller förlänger inte provperioden.</p>}
            {error && <Alert variant="danger" role="alert">{error}{!packs && <Button variant="link" onClick={load}>Hämta igen</Button>}</Alert>}
            {!packs && !error && <p role="status"><Spinner as="span" animation="border" size="sm" /> Hämtar kreditpaket…</p>}
            {packs?.length === 0 && <p className="feature-description mb-0">Inga kreditpaket finns till försäljning just nu.</p>}
            <div className="feature-cards">{packs?.map(pack => <div className="feature-card premium" key={pack.package_version}>
                <div className="feature-icon premium"><Bot size={18} /></div>
                <div className="feature-content"><div className="feature-header"><h3 className="feature-name">{formatMicrocredits(pack.credits_microcredits)} AI-krediter</h3></div>
                    <p className="feature-description">Extra krediter att använda med dina AI-funktioner.</p>
                    <div className="feature-benefits"><span className="benefit-tag">{money(pack)} exkl. moms</span><span className="benefit-tag">Engångsköp</span>
                        <Button variant="outline-primary" size="sm" className="package-buy" aria-label={`Köp ${formatMicrocredits(pack.credits_microcredits)} AI-krediter`} disabled={!!busy || !isOwner || entitlements?.billingAccess?.allowed === false} onClick={() => buy(pack)}>{busy === pack.package_version ? 'Öppnar betalning…' : 'Köp'}</Button>
                    </div>
                </div>
            </div>)}</div>
            {!isOwner && <p className="feature-description mt-3 mb-0">Företagets ägare kan köpa extra AI-krediter.</p>}
            {isOwner && entitlements?.billingAccess?.allowed === false && <p className="feature-description mt-3 mb-0">En aktiv prenumeration eller provperiod krävs för att köpa extra AI-krediter.</p>}
        </div>
    </section>;
}
